// services/htmlPersistence.ts
import { StoryChiefPost } from '../types/blog';

export interface HTMLGenerationOptions {
  siteName?: string;
  siteUrl?: string;
  defaultAuthor?: string;
  logoUrl?: string;
  cacheMaxAge?: number;
}

export interface HTMLFileInfo {
  exists: boolean;
  size?: number;
  modified?: string;
  created?: string;
  isStale?: boolean;
}

class HTMLPersistenceService {
  private options: HTMLGenerationOptions;

  constructor(options: HTMLGenerationOptions = {}) {
    this.options = {
      siteName: 'Your Site Name',
      siteUrl: 'https://yoursite.com',
      defaultAuthor: 'Your Company',
      logoUrl: '/logo.png',
      cacheMaxAge: 24,
      ...options
    };
  }

  // Check if HTML file exists and get its info
  async getHTMLFileInfo(slug: string): Promise<HTMLFileInfo> {
    try {
      const response = await fetch(`/api/check-html/${slug}`);
      
      if (!response.ok) {
        return { exists: false };
      }

      const data = await response.json();
      
      if (!data.exists) {
        return { exists: false };
      }

      return {
        exists: true,
        isStale: false // Server handles staleness
      };

    } catch (error) {
      console.error('Error checking HTML file info:', error);
      return { exists: false };
    }
  }

  // Get post content from cache if it exists and is fresh
  async getPostFromHTML(slug: string): Promise<StoryChiefPost | null> {
    try {
      console.log(`🔍 Checking cache for slug: ${slug}`);
      
      const fileInfo = await this.getHTMLFileInfo(slug);
      console.log(`📁 File info for ${slug}:`, fileInfo);
      
      if (!fileInfo.exists || fileInfo.isStale) {
        console.log(`❌ HTML file for ${slug} ${!fileInfo.exists ? 'does not exist' : 'is stale'}`);
        return null;
      }

      console.log(`🔄 Attempting to fetch cached post data for ${slug}`);
      
      // Try to get cached post data
      const response = await fetch(`/api/cached-post/${slug}`);
      console.log(`📡 Cache API response status: ${response.status}`);
      
      if (!response.ok) {
        console.log(`❌ No cached post data for ${slug} - API returned ${response.status}`);
        return null;
      }

      const cachedPost = await response.json();
      console.log(`✅ Successfully retrieved cached post data for: ${slug}`);
      
      return cachedPost;

    } catch (error) {
      console.error(`💥 Error getting post from cache for ${slug}:`, error);
      return null;
    }
  }

  // Save HTML file and cache post data
  async savePostHTML(post: StoryChiefPost): Promise<void> {
    try {
      const html = this.generatePostHTML(post);
      const filename = `${post.slug}.html`;
      
      const response = await fetch('/api/save-post-html', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename,
          html,
          slug: post.slug,
          title: post.title,
          postData: post
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to save HTML file: ${response.statusText}`);
      }

      console.log(`💾 Saved HTML and cached data for: ${post.title}`);
    } catch (error) {
      console.error('Error saving HTML file:', error);
      throw error;
    }
  }

  // Check if HTML file exists for a post (with staleness check)
  async checkHTMLExists(slug: string): Promise<boolean> {
    const fileInfo = await this.getHTMLFileInfo(slug);
    return fileInfo.exists && !fileInfo.isStale;
  }

  // Generate complete HTML for a blog post
  generatePostHTML(post: StoryChiefPost): string {
    const featuredImage = this.getPostImage(post);
    const keywords = this.getPostKeywords(post);
    const publishedDate = new Date(post.published_at).toISOString();
    const modifiedDate = new Date(post.updated_at).toISOString();
    const postUrl = `${this.options.siteUrl}/blog/${post.slug}`;

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  
  <!-- SEO Meta Tags -->
  <title>${post.seo?.title || post.title} | ${this.options.siteName}</title>
  <meta name="description" content="${post.seo?.description || post.excerpt || ''}">
  <meta name="keywords" content="${keywords.join(', ')}">
  <meta name="author" content="${this.options.defaultAuthor}">
  <meta name="robots" content="index, follow">
  <link rel="canonical" href="${postUrl}">
  
  <!-- Open Graph Meta Tags -->
  <meta property="og:type" content="article">
  <meta property="og:title" content="${post.title}">
  <meta property="og:description" content="${post.excerpt || ''}">
  <meta property="og:url" content="${postUrl}">
  <meta property="og:site_name" content="${this.options.siteName}">
  <meta property="og:published_time" content="${publishedDate}">
  <meta property="og:modified_time" content="${modifiedDate}">
  ${featuredImage ? `<meta property="og:image" content="${featuredImage}">` : ''}
  ${featuredImage ? `<meta property="og:image:alt" content="${this.getImageAlt(post)}">` : ''}
  
  <!-- Twitter Card Meta Tags -->
  <meta name="twitter:card" content="summary_large_image">
  <meta name="twitter:title" content="${post.title}">
  <meta name="twitter:description" content="${post.excerpt || ''}">
  ${featuredImage ? `<meta name="twitter:image" content="${featuredImage}">` : ''}
  
  <!-- Structured Data (JSON-LD) -->
  <script type="application/ld+json">
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "${post.title}",
    "description": "${post.excerpt || ''}",
    "image": ${featuredImage ? `"${featuredImage}"` : 'null'},
    "author": {
      "@type": "Organization",
      "name": "${this.options.defaultAuthor}"
    },
    "publisher": {
      "@type": "Organization",
      "name": "${this.options.siteName}",
      "logo": {
        "@type": "ImageObject",
        "url": "${this.options.siteUrl}${this.options.logoUrl}"
      }
    },
    "datePublished": "${publishedDate}",
    "dateModified": "${modifiedDate}",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "${postUrl}"
    },
    "url": "${postUrl}",
    "keywords": "${keywords.join(', ')}"
  }
  </script>
  
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      background-color: #fff;
    }
    
    .header {
      text-align: center;
      margin-bottom: 40px;
      padding-bottom: 20px;
      border-bottom: 1px solid #eee;
    }
    
    .site-name {
      color: #666;
      font-size: 14px;
      text-decoration: none;
      margin-bottom: 10px;
      display: inline-block;
    }
    
    .post-meta {
      color: #666;
      font-size: 14px;
      margin-bottom: 20px;
    }
    
    .post-title {
      font-size: 2.5em;
      font-weight: 700;
      margin: 20px 0;
      line-height: 1.2;
    }
    
    .post-excerpt {
      font-size: 1.2em;
      color: #666;
      font-style: italic;
      margin-bottom: 30px;
    }
    
    .featured-image {
      width: 100%;
      height: auto;
      border-radius: 8px;
      margin-bottom: 30px;
    }
    
    .keywords {
      margin: 20px 0;
    }
    
    .keyword-tag {
      display: inline-block;
      background: #f0f0f0;
      color: #333;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      margin: 2px;
    }
    
    .post-content {
      font-size: 16px;
      line-height: 1.8;
    }
    
    .post-content img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
      margin: 20px 0;
    }
    
    .post-content h1, .post-content h2, .post-content h3, 
    .post-content h4, .post-content h5, .post-content h6 {
      margin-top: 40px;
      margin-bottom: 20px;
      font-weight: 600;
    }
    
    .post-content p {
      margin-bottom: 20px;
    }
    
    .post-content blockquote {
      border-left: 4px solid #3f51b5;
      padding-left: 20px;
      margin: 20px 0;
      font-style: italic;
      color: #666;
    }
    
    .post-content code {
      background: #f5f5f5;
      padding: 2px 6px;
      border-radius: 3px;
      font-family: 'Monaco', 'Consolas', monospace;
      font-size: 14px;
    }
    
    .post-content pre {
      background: #f5f5f5;
      padding: 20px;
      border-radius: 6px;
      overflow-x: auto;
      margin: 20px 0;
    }
    
    .back-link {
      display: inline-block;
      margin-top: 40px;
      color: #3f51b5;
      text-decoration: none;
      font-weight: 500;
    }
    
    .back-link:hover {
      text-decoration: underline;
    }
    
    @media (max-width: 600px) {
      body {
        padding: 15px;
      }
      
      .post-title {
        font-size: 2em;
      }
      
      .post-excerpt {
        font-size: 1.1em;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <a href="/" class="site-name">${this.options.siteName}</a>
  </div>
  
  <article>
    <div class="post-meta">
      <time datetime="${publishedDate}">
        ${this.formatDate(post.published_at)}
      </time>
    </div>
    
    <h1 class="post-title">${post.title}</h1>
    
    ${post.excerpt ? `<div class="post-excerpt">${post.excerpt}</div>` : ''}
    
    ${featuredImage ? `
      <img 
        src="${featuredImage}" 
        alt="${this.getImageAlt(post)}" 
        class="featured-image"
      >
    ` : ''}
    
    ${keywords.length > 0 ? `
      <div class="keywords">
        ${keywords.map(keyword => `<span class="keyword-tag">${keyword}</span>`).join('')}
      </div>
    ` : ''}
    
    <div class="post-content">
      ${post.content}
    </div>
    
    <a href="/blog" class="back-link">← Back to Blog</a>
  </article>
</body>
</html>`;
  }

  // Helper methods
  private getPostImage(post: StoryChiefPost): string | null {
    if (post?.featured_image?.data?.url) {
      return post.featured_image.data.url;
    }
    
    if (post?.featured_image?.data?.sizes) {
      const sizes = post.featured_image.data.sizes;
      return sizes.large || sizes.regular || sizes.full;
    }
    
    return null;
  }

  private getImageAlt(post: StoryChiefPost): string {
    return post?.featured_image?.data?.alt || 
           post?.featured_image?.data?.name || 
           post?.title || 
           'Blog post image';
  }

  private getPostKeywords(post: StoryChiefPost): string[] {
    if (!post.seo_keyword) return [];
    return post.seo_keyword.split(',').map(k => k.trim()).filter(k => k);
  }

  private formatDate(dateString: string): string {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date unavailable';
    }
  }
}

export const htmlPersistence = new HTMLPersistenceService({
  siteName: 'Recruitment Intelligence',
  siteUrl: 'https://recruitmentintelligence.com',
  defaultAuthor: 'RECRUITMENT INTELLIGENCE',
  logoUrl: 'assets/RIC_NoText.png',
  cacheMaxAge: 24
});